import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../api/client';
import type { Deck } from '../api/types';
import { navigate } from '../lib/router-state';
import { setPageTitle } from '../lib/page-title';

import '../components/deck-card';

function showToast(message: string) {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  document.body.appendChild(el);
  window.setTimeout(() => el.remove(), 2400);
}

/**
 * Landing page. Fetches the deck list on mount and renders a grid of
 * deck cards. Loading, error and empty states are handled inline.
 *
 * Rendering into light DOM lets the global stylesheet (page container,
 * grid, state classes) apply directly without re-importing tokens.
 */
@customElement('deck-list-page')
export class DeckListPage extends LitElement {
  @state() private decks: Deck[] | null = null;
  @state() private error: string | null = null;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    setPageTitle('Decks', 'Browse and organise your Folio decks.');
    void this.load();
  }

  private async load() {
    this.error = null;
    try {
      this.decks = await api.listDecks();
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load decks';
      this.decks = [];
    }
  }

  private goCreate = () => navigate('/new');

  /** Resume a draft in the builder rather than opening the read-only viewer. */
  private resumeDraft(deck: Deck) {
    navigate(`/new/${encodeURIComponent(deck.id)}`);
  }

  /** Delete a deck (drafts included — "draft until deleted"). Confirms first,
   *  then removes it from the list optimistically. Errors re-sync via reload. */
  private async deleteDeck(deck: Deck) {
    const label = deck.status === 'draft' ? 'draft' : 'deck';
    const ok = window.confirm(
      `Delete this ${label}${deck.title ? ` — "${deck.title}"` : ''}? This cannot be undone.`,
    );
    if (!ok) return;
    try {
      await api.deleteDeck(deck.id);
      this.decks = (this.decks ?? []).filter((d) => d.id !== deck.id);
      showToast('Deck deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      showToast(`Delete failed: ${msg}`);
      await this.load();
    }
  }

  render() {
    return html`
      <section class="page">
        <div class="page-head">
          <div class="titles">
            <h1>Decks</h1>
            <p class="page-sub">Workplace by Ricoh · presentation library</p>
          </div>
          <button class="btn-primary" type="button" @click=${this.goCreate}>New deck</button>
        </div>
        ${this.renderBody()}
      </section>
    `;
  }

  private renderBody() {
    if (this.error) {
      return html`<div class="state error">Couldn't load decks — ${this.error}</div>`;
    }
    if (this.decks === null) {
      return html`<div class="state">Loading decks…</div>`;
    }
    if (this.decks.length === 0) {
      return html`
        <div class="state">
          <p>No decks yet.</p>
          <button class="btn-primary" type="button" @click=${this.goCreate}>Create your first deck</button>
        </div>
      `;
    }
    return html`
      <div class="deck-grid">
        ${this.decks.map((d) => this.renderCard(d))}
      </div>
    `;
  }

  /** A single grid item: the live deck-card, plus a "Draft" pill overlay and a
   *  small action row (Resume editing + Delete) for drafts. Saved decks render
   *  the card alone, keeping the existing list UI unchanged. */
  private renderCard(d: Deck) {
    const isDraft = d.status === 'draft';
    if (!isDraft) {
      return html`
        <div class="deck-cell">
          <deck-card .deck=${d}></deck-card>
          <div class="deck-cell-actions">
            <button
              type="button"
              class="cell-action danger"
              @click=${() => this.deleteDeck(d)}
            >Delete</button>
          </div>
        </div>
      `;
    }
    return html`
      <div class="deck-cell is-draft">
        <span class="draft-pill">Draft</span>
        <deck-card .deck=${d}></deck-card>
        <div class="deck-cell-actions">
          <button
            type="button"
            class="cell-action"
            @click=${() => this.resumeDraft(d)}
          >Resume editing</button>
          <button
            type="button"
            class="cell-action danger"
            @click=${() => this.deleteDeck(d)}
          >Delete</button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'deck-list-page': DeckListPage;
  }
}
